import React, { useState } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Collapse,
    Chip,
} from '@mui/material';
import { AttachMoney, Timeline, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import SummaryCards from '../components/SummaryCards';
import { formatCurrency, getStatusColor, formatStatusValue } from '../constants';

const CompanyRow = ({ company }) => {
    const [open, setOpen] = useState(false);

    // Define statuses with deferred cost liability (must match backend definition)
    // Excludes SUBMITTED (not committed), COMPLETED, and REJECTED
    const DEFERRED_COST_STATUSES = [
        'ACCEPTED',
        'ASSIGNED',
        'WORK_COMPLETED',
        'INTERNALLY_SIGNED_OFF',
    ];

    // Filter to show only deficiencies with deferred cost liability
    const displayedDeficiencies = company.deficiencies.filter((d) =>
        DEFERRED_COST_STATUSES.includes(d.status)
    );

    return (
        <>
            <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
                <TableCell>
                    <IconButton size="small" onClick={() => setOpen(!open)}>
                        {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                    </IconButton>
                </TableCell>
                <TableCell>{company.companyName}</TableCell>
                <TableCell align="right">{company.openCount}</TableCell>
                <TableCell align="right">
                    {formatCurrency(company.totalDeferredCostOfOpenItems)}
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={4}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 2 }}>
                            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                                Deficiency Details
                            </Typography>
                            <Table size="small" sx={{ mt: 1 }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell
                                            sx={{
                                                fontWeight: 600,
                                                color: 'text.secondary',
                                                fontSize: '0.75rem',
                                            }}
                                        >
                                            Title
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                fontWeight: 600,
                                                color: 'text.secondary',
                                                fontSize: '0.75rem',
                                            }}
                                        >
                                            Status
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                fontWeight: 600,
                                                color: 'text.secondary',
                                                fontSize: '0.75rem',
                                            }}
                                        >
                                            Location
                                        </TableCell>
                                        <TableCell
                                            align="right"
                                            sx={{
                                                fontWeight: 600,
                                                color: 'text.secondary',
                                                fontSize: '0.75rem',
                                            }}
                                        >
                                            Deferred Cost
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {displayedDeficiencies.slice(0, 10).map((deficiency) => (
                                        <TableRow key={deficiency.deficiencyId}>
                                            <TableCell>
                                                <Typography
                                                    variant="body2"
                                                    noWrap
                                                    sx={{ maxWidth: 200 }}
                                                >
                                                    {deficiency.title}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={formatStatusValue(deficiency.status)}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: getStatusColor(deficiency.status),
                                                        color: 'white',
                                                        fontSize: '0.7rem',
                                                        fontWeight: 600,
                                                        borderRadius: 1,
                                                        height: 20,
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="text.secondary">
                                                    {deficiency.floorplanName || 'N/A'}
                                                    {deficiency.roiName &&
                                                        ` - ${deficiency.roiName}`}
                                                </Typography>
                                            </TableCell>
                                            <TableCell
                                                align="right"
                                                sx={{ fontFamily: 'monospace' }}
                                            >
                                                {formatCurrency(deficiency.deferredCost)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {displayedDeficiencies.length > 10 && (
                                        <TableRow>
                                            <TableCell colSpan={4} align="center">
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    Showing 10 of {displayedDeficiencies.length}{' '}
                                                    deficiencies
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};

const DeferredCostView = ({ reportData }) => {
    if (!reportData) return null;

    const { overallSummary = {}, companyBreakdowns = [] } = reportData;

    // Always filter to show only companies with open items
    const filteredCompanies = companyBreakdowns.filter((c) => c.openCount > 0);

    const summaryCards = [
        {
            title: 'Open Items with Cost',
            value: overallSummary.openItemsWithCost,
            icon: Timeline,
            color: 'primary',
        },
        {
            title: 'Deferred Cost',
            value: formatCurrency(overallSummary.totalDeferredCostOfOpenItems),
            icon: AttachMoney,
            color: 'error',
        },
    ];

    return (
        <Box>
            <SummaryCards cards={summaryCards} />

            <Box sx={{ mt: 4 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, textAlign: 'left' }}>
                    Deferred Cost by Company
                </Typography>
                <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{ border: '1px solid', borderColor: 'divider' }}
                >
                    <Table sx={{ minWidth: 650 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell />
                                <TableCell
                                    sx={{
                                        fontWeight: 600,
                                        color: 'text.secondary',
                                        textTransform: 'uppercase',
                                        fontSize: '0.75rem',
                                        letterSpacing: '0.5px',
                                    }}
                                >
                                    Company
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{
                                        fontWeight: 600,
                                        color: 'text.secondary',
                                        textTransform: 'uppercase',
                                        fontSize: '0.75rem',
                                        letterSpacing: '0.5px',
                                    }}
                                >
                                    Open Items
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{
                                        fontWeight: 600,
                                        color: 'text.secondary',
                                        textTransform: 'uppercase',
                                        fontSize: '0.75rem',
                                        letterSpacing: '0.5px',
                                    }}
                                >
                                    Open Cost
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredCompanies.map((company) => (
                                <CompanyRow key={company.companyId} company={company} />
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </Box>
    );
};

export default DeferredCostView;
